import { wcagContrastChecker } from "@mdhnpm/wcag-contrast-checker";
import tinycolor from "tinycolor2";

/** The neutral grey the non-themed styles use for subtle surfaces such as slider rails. */
const neutralPaleColour = "#e5e7eb";

/** Left nav sits on white. Label contrast is judged against that surface. */
const navBackgroundColour = "#ffffff";

/** Unselected nav labels. Used when the tenant primary fails 4.5:1 on the nav. */
const navLabelColour = "#6b7280";

const toHexColour = (colour: string): string | null => {
  if (!colour || typeof colour !== "string") return null;
  const withHash = colour.startsWith("#") ? colour : `#${colour}`;
  const parsed = tinycolor(withHash);
  return parsed.isValid() ? parsed.toHexString() : null;
};

/** How far a pale colour has to sit from white before we treat it as usable on a white
 * background. Deliberately close to 1 (no contrast at all): paling a colour tips lighter
 * brand colours all the way to white, and this only rescues those, so clients whose colour
 * still carries any visible tint keep it. */
const minimumContrastAgainstWhite = 1.02;

/** Judged on the translucent rendering, since that is the weakest way a pale colour gets used,
 * and it keeps every surface in a component falling back together rather than one at a time. */
const paleVersionIsVisibleOnWhite = (paleVersion: string): boolean => {
  if (!paleVersion) return false;

  const asRenderedOnWhite = tinycolor.mix(paleVersion, "#ffffff", 50);
  return (
    tinycolor.readability(asRenderedOnWhite, "#ffffff") >=
    minimumContrastAgainstWhite
  );
};

const colourHelper = {
  getDefaultTextColour: function (): string {
    return "#FFFFFF";
  },
  /** Compares the given colour (background) against white text to see if it is accessible */
  doesColourPassContrastCheck: function (
    foregroundColour: string,
    backgroundColour: string
  ): boolean {
    try {
      let contrastChecker = wcagContrastChecker(
        foregroundColour,
        backgroundColour
      );
      return contrastChecker.regularText.aa;
    } catch (e) {
      return false;
    }
  },
  /** Checks whether the hex colour provided is a valid hex */
  isValidHex: function (colour: string): boolean {
    if (!colour || typeof colour !== "string") return false;

    // Validate hex values
    if (colour.substring(0, 1) === "#") colour = colour.substring(1);

    switch (colour.length) {
      case 3:
        return /^[0-9A-F]{3}$/i.test(colour);
      case 6:
        return /^[0-9A-F]{6}$/i.test(colour);
      case 8:
        return /^[0-9A-F]{8}$/i.test(colour);
      default:
        return false;
    }
  },

  /** Takes a hex colour and lightens/darkens it (positive number lightens, negative darkens).
   * Returns the new hex colour with the # at the start
   */
  shadeColour: function (colour: string, percent: number): string {
    if (!colour || typeof colour !== "string") return colour;

    const colourObj = tinycolor(colour);
    if (percent > 0) {
      return colourObj.lighten(percent).toHexString();
    }
    return colourObj.darken(Math.abs(percent)).toHexString();
  },

  /** Takes a hex colour and creates a pale version of it by lightening and desaturating
   * @param hex - The hex colour string (e.g. "#d40032")
   * @returns The pale version of the colour (e.g. "#fef2f2")
   */
  createPaleVersion: function (hex: string): string {
    if (!hex || typeof hex !== "string") return "";
    const colourCode = hex.startsWith("#") ? hex : `#${hex}`;
    const colourObj = tinycolor(colourCode);
    // Lighten the color significantly and desaturate to make it more pastel
    return colourObj.lighten(50).desaturate(30).toHexString();
  },

  /** Takes a hex colour and creates a pale version of it by lightening and desaturating
   * and then adds an opacity of 20%
   * @param hex - The hex colour string (e.g. "#d40032")
   * @returns The pale version of the colour with an opacity of 20% (e.g. "#fef2f2")
   */
  createPaleVersionWithOpacity: function (hex: string): string {
    const paleVersion = this.createPaleVersion(hex);
    return `${paleVersion}80`;
  },

  /** As createPaleVersion, but for surfaces that sit on a white background and have to stay
   * visible. Lighter primary colours pale all the way to white, leaving the surface
   * indistinguishable from the page behind it, so those fall back to the neutral grey the
   * non-themed styles use.
   * @param hex - The hex colour string (e.g. "#d40032")
   */
  createVisiblePaleVersion: function (hex: string): string {
    const paleVersion = this.createPaleVersion(hex);
    return paleVersionIsVisibleOnWhite(paleVersion)
      ? paleVersion
      : neutralPaleColour;
  },

  /** The translucent counterpart of createVisiblePaleVersion. Falls back on the same colours,
   * so a component using both keeps them consistent.
   * @param hex - The hex colour string (e.g. "#d40032")
   */
  createVisiblePaleVersionWithOpacity: function (hex: string): string {
    const paleVersion = this.createPaleVersion(hex);
    return paleVersionIsVisibleOnWhite(paleVersion)
      ? this.createPaleVersionWithOpacity(hex)
      : neutralPaleColour;
  },

  /**
   * Tenant primary when it meets WCAG AA (4.5:1) on the white nav, otherwise the
   * existing nav label colour. Chosen once when the theme is applied.
   */
  navSelectedLabelColour: function (primaryColour: string): string {
    const primary = toHexColour(primaryColour);
    if (!primary) return navLabelColour;
    return this.doesColourPassContrastCheck(primary, navBackgroundColour)
      ? primary
      : navLabelColour;
  },

  /** Takes a hex colour and returns a numeric csv for the RGB, e.g. 123, 32, 32 */
  convertHexToRgbNumbersString: function (hex: string): string {
    if (!hex || typeof hex !== "string") return "";
    const colourObj = tinycolor(hex);
    const rgb = colourObj.toRgb();
    return `${rgb.r},${rgb.g},${rgb.b}`;
  },

  /**
   * Prepares a ColourPicker value for API submit.
   * Trims and uppercases; returns null when empty, whitespace-only, or just "#"
   * (ColourPicker emits "#" when the textbox is cleared).
   */
  normaliseHexForSubmit: function (
    colour: string | null | undefined
  ): string | null {
    if (colour == null || typeof colour !== "string") return null;
    const trimmed = colour.trim();
    if (!trimmed || trimmed === "#") return null;
    const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    return withHash.toUpperCase();
  },
};

export default colourHelper;
